/**
 * Variant grouping RULE for an import source: groups rows the store itself
 * does not group, by a key computed from each row's reference string.
 *
 * - `stem`     — the first N characters ("SBDC101" / "SBDC103", N=4 → "SBDC").
 * - `splitter` — everything before the first of the given characters
 *                ("W-BEL-GYS" with "-" → "W").
 * - `regex`    — capture group 1 of the pattern (or the whole match).
 *
 * Only top-level rows take part: a row the store already groups (a Shopify
 * product's variant, an expanded WooCommerce / Square option) keeps its store
 * parent, and depth stays one. A proposed group imports nothing different
 * until the curator accepts it in the preview; then its chosen parent row is
 * written first, as an ordinary reference, and every other member is written
 * as its variant (parentReferenceId = the parent's doc id). No synthetic
 * parent doc is created.
 *
 * Pure (no React, no Firestore) so the rule is pinned by tests.
 */
import type { VariantGroupingRule } from '../../types';

/** The rule is set and would group something. */
export function ruleIsActive(rule: VariantGroupingRule | undefined): rule is VariantGroupingRule {
  return !!rule && rule.type !== 'none' && validateRule(rule) === null;
}

/** Why the rule can't run, or null when it can (a `none` rule is always valid). */
export function validateRule(rule: VariantGroupingRule): string | null {
  const value = rule.value ?? '';
  switch (rule.type) {
    case 'none':
      return null;
    case 'stem': {
      const n = Number(value.trim());
      return Number.isInteger(n) && n >= 1 && n <= 64 ? null : 'Enter a whole number of characters (1–64).';
    }
    case 'splitter':
      return value.length > 0 ? null : 'Enter at least one splitter character.';
    case 'regex':
      if (!value.trim()) return 'Enter a pattern.';
      try {
        new RegExp(value);
        return null;
      } catch (err) {
        return `Invalid pattern: ${err instanceof Error ? err.message : String(err)}`;
      }
  }
}

/**
 * The group key for one reference string, or null when the rule doesn't give
 * this row a key: the ref is shorter than the stem, has no splitter character,
 * or the pattern doesn't match. Keys compare case-insensitively.
 */
export function ruleGroupKey(reference: string, rule: VariantGroupingRule): string | null {
  const ref = reference.trim();
  if (!ref || !ruleIsActive(rule)) return null;
  const value = rule.value ?? '';
  let key: string | undefined;
  switch (rule.type) {
    case 'stem': {
      const n = Number(value.trim());
      // A ref no longer than the stem IS the stem — it groups with its longer siblings.
      key = ref.length >= n ? ref.slice(0, n) : undefined;
      break;
    }
    case 'splitter': {
      let cut = -1;
      for (const ch of value) {
        const at = ref.indexOf(ch);
        if (at > 0 && (cut < 0 || at < cut)) cut = at;
      }
      key = cut > 0 ? ref.slice(0, cut) : undefined;
      break;
    }
    case 'regex': {
      const m = new RegExp(value).exec(ref);
      key = m ? (m[1] ?? m[0]) : undefined;
      break;
    }
  }
  const k = key?.trim();
  return k ? k.toUpperCase() : null;
}

export interface RuleGroupRow {
  reference: string;
  /** Ticked to import. */
  included: boolean;
  /** Already grouped by the store (its own parent) — never re-grouped by the rule. */
  storeGrouped: boolean;
}

export interface RuleGroup {
  key: string;
  /** Row indices, in row order. */
  members: number[];
  /** The member proposed as parent. */
  parentIndex: number;
}

/**
 * Default parent: the shortest reference (the bare model number usually is),
 * then alphabetical, then row order.
 */
export function defaultParentIndex(members: readonly number[], refOf: (i: number) => string): number {
  return [...members].sort((a, b) => {
    const ra = refOf(a).trim();
    const rb = refOf(b).trim();
    return ra.length - rb.length || ra.localeCompare(rb) || a - b;
  })[0];
}

/** Every group of 2+ eligible rows the rule proposes, ordered by key. */
export function proposeRuleGroups(rows: readonly RuleGroupRow[], rule: VariantGroupingRule | undefined): RuleGroup[] {
  if (!ruleIsActive(rule)) return [];
  const byKey = new Map<string, number[]>();
  rows.forEach((row, i) => {
    if (!row.included || row.storeGrouped) return;
    const key = ruleGroupKey(row.reference, rule);
    if (!key) return;
    byKey.set(key, [...(byKey.get(key) ?? []), i]);
  });
  return [...byKey.entries()]
    .filter(([, members]) => members.length >= 2)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, members]) => ({ key, members, parentIndex: defaultParentIndex(members, (i) => rows[i].reference) }));
}

/** The curator's call on one proposed group in the preview. */
export interface RuleGroupDecision {
  status: 'proposed' | 'accepted' | 'rejected';
  /** Overrides the proposed parent; must still be a member. */
  parentIndex?: number;
  /** Members split off the group — they import standalone. */
  splitOff?: number[];
}

export interface ResolvedRuleGroup {
  key: string;
  parentIndex: number;
  /** Members other than the parent. */
  variantIndices: number[];
}

/**
 * The accepted groups as they will be written: split-off members removed, the
 * parent re-picked when it was split off itself, and any group left with fewer
 * than two members dropped.
 */
export function resolveAcceptedGroups(
  groups: readonly RuleGroup[],
  decisions: ReadonlyMap<string, RuleGroupDecision>,
  refOf: (i: number) => string,
): ResolvedRuleGroup[] {
  const out: ResolvedRuleGroup[] = [];
  for (const g of groups) {
    const d = decisions.get(g.key);
    if (d?.status !== 'accepted') continue;
    const members = effectiveMembers(g, d);
    if (members.length < 2) continue;
    const parentIndex = effectiveParent(g, d, refOf);
    out.push({ key: g.key, parentIndex, variantIndices: members.filter((i) => i !== parentIndex) });
  }
  return out;
}

/** The group's members minus the ones split off. */
export function effectiveMembers(group: RuleGroup, decision: RuleGroupDecision | undefined): number[] {
  const split = new Set(decision?.splitOff ?? []);
  return group.members.filter((i) => !split.has(i));
}

/** The parent the group will use, after the curator's pick and splits. */
export function effectiveParent(
  group: RuleGroup,
  decision: RuleGroupDecision | undefined,
  refOf: (i: number) => string,
): number {
  const members = effectiveMembers(group, decision);
  const picked = decision?.parentIndex ?? group.parentIndex;
  return members.includes(picked) ? picked : defaultParentIndex(members, refOf);
}
